import { Component } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { map, startWith, Subject, takeUntil } from 'rxjs';
import { itemsVM } from '../../../Model/items/itemsVM';
import { UserType } from '../../../Shared/Enums/UserType';
import { AuthService } from '../../../Shared/Services/auth.service';
import { CustomToastrService } from '../../../Shared/Services/custom-toastr.service';
import { DialogService } from '../../../Shared/Services/dialog.service';
import { DtoAgentVM } from '../../../Model/agents/DtoAgentVM';
import { DtoClientForViewVM } from '../../../Model/clients/DtoClientForViewVM';
import { BuySellOrderService } from '../../../Services/buy-sell-order.service';
import { SharedService } from '../../../Shared/Services/shared.service';
import { DtoBuySellOrderVm } from '../../../Model/items/DtoBuySellOrderVm';
import * as arabicStrings from '@flowdegree/arabic-strings';

@Component({
  selector: 'app-sell-order',
  templateUrl: './sell-order.component.html',
  styleUrl: './sell-order.component.scss'
})
export class SellOrderComponent {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  form!: FormGroup;
  AllItems!: itemsVM[];
  AllClients: DtoClientForViewVM[] = [];
  DtoBuySellOrder!: DtoBuySellOrderVm;
  showInvoice = false;
  filteredOptions: any[] = [];
  filteredClientsOptions: DtoClientForViewVM[] = [];

  constructor(private fb: FormBuilder, private api: BuySellOrderService, private auth: AuthService, private spinner: NgxSpinnerService,
    private router: Router, private toastrService: CustomToastrService, private dialogService: DialogService,
    private shared: SharedService) { }

  ngOnInit() {
    if (this.auth.isAuthenticated([UserType.Agent])) {
      this.spinner.show();
      this.initForm();
      this.GetLookups();
    }
    else {
      this.router.navigateByUrl('dashboard/not-authorized');
    }
  }

  initForm() {
    this.form = this.fb.group({
      clientId: ['', Validators.required],
      items: this.fb.array([])
    });

    this.addItem();
  }

  get f() {
    return this.form.controls;
  }

  get items() {
    return this.form.controls["items"] as FormArray;
  }

  get GetItems() {
    return (this.form.controls["items"] as FormArray).controls as FormGroup[];
  }

  GetLookups() {
    this.GetAllItems();
    this.GetAllClients();
  }

  GetAllItems() {
    if (this.auth.isAuthenticated([UserType.Agent])) {
      this.api.GetAllItems()
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          this.AllItems = res.payload;
          this.spinner.hide();
          this.ManageNameControl(this.AllItems, 0, "productId", "productName");
        });
    }
    else {
      this.router.navigateByUrl('dashboard/not-authorized');
    }
  }

  GetAllClients() {
    if (this.auth.isAuthenticated([UserType.Agent])) {
      this.api.GetAllClients()
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          {
            next: (res: any) => {
              this.AllClients = res.payload;
              this.spinner.hide();
              this.ManageAllClientControl("clientId", "name");
            },
            error: (error: any) => {
              this.spinner.hide();
              this.toastrService.showErrorToast('Error', "SomethingWentWrong");
            }
          });
    } else {
      this.router.navigateByUrl('dashboard/not-authorized');
    }
  }

  deleteItem(lessonIndex: number) {
    this.items.removeAt(lessonIndex);
  }

  addItem() {
    const itemForm = this.fb.group({
      productId: ['', Validators.required],
      count: ['', Validators.required],
    });

    this.items.push(itemForm);
    this.ManageNameControl(this.AllItems, this.items.length - 1, "productId", "productName");
  }

  ManageNameControl(list: any[], index: number, control: any, column: any) {
    var arrayControl = this.form.get('items') as FormArray;
    if (list) {
      this.filteredOptions[index] = arrayControl.at(index).get(control)?.valueChanges
        .pipe(
          startWith<string | any>(''),
          map(value => typeof value === 'string' ? value : value.column),
          map(name => name ? this.filterAutocomplete(list, name, column) : list.slice())
        );
    }
  }

  ManageAllClientControl(control: any, column: any) {
    if (this.AllClients) {
      this.form.controls[control]?.valueChanges
        .pipe(
          startWith<string | any>(''),
          map(value => typeof value === 'string' ? value : value.column),
          map(name => name ? this.filterAutocomplete(this.AllClients, name, column) : this.AllClients.slice())
        ).subscribe({
          next: (res: any) => {
            this.filteredClientsOptions = res;
          },
          error: (error: any) => {
            this.spinner.hide();
            this.toastrService.showErrorToast('Error', "SomethingWentWrong");
          }
        });
    }
  }

  filterAutocomplete(list: any[], name: string, column: any): any[] {
    name = arabicStrings.sanitize(name!);
    const filterValue = name.toLowerCase();
    return list.filter((option: any) => arabicStrings.sanitize(option[column]!.toLowerCase()).indexOf(filterValue) === 0);
  }

  displayItemsFn(itemID?: any): any {
    return this.AllItems?.find(item => item.productId === itemID)?.fullName;
  }

  displayClientFn(itemID?: any): any {
    return this.AllClients?.find(item => item.id === itemID)?.name;
  }

  submit() {
    if (this.form.valid) {
      this.dialogService.confirmDialog({
        titleKey: 'confirmation',
        messageKey: 'AreYouSuretoSaveThis',
        confirmTextKey: 'Yes',
        cancelTextKey: 'No'
      }).subscribe(isConfirmed => {
        if (isConfirmed) {
          this.SubmitSellOrder(this.form.value);
        }
      });
    }
    else {
      this.getFormValidationErrors(this.form);
    }
  }

  getFormValidationErrors(form: FormGroup) {
    Object.keys(this.form.controls).forEach(key => {
      let formGroups = (this.form.controls["items"] as FormArray).controls as FormGroup[];
      if (formGroups) {
        formGroups.forEach(formGroup => {
          Object.keys(formGroup?.controls).forEach(control => {
            this.shared.FireControlValidation(formGroup, control);
          });
        });
      }
      else {
        this.shared.FireControlValidation(this.form, key);
      }
    });
  }

  SubmitSellOrder(form: any) {
    this.spinner.show();
    this.api.InsertSellOrder(form).pipe(takeUntil(this.destroy$))
      .subscribe(
        {
          next: (data: any) => {
            this.spinner.hide();
            if (data.isSuccess) {
              this.toastrService.showSuccessToast('Success', 'AddedSuccessfully');
              this.DtoBuySellOrder = data.payload;
              this.showInvoice = true;
            } else {
              this.toastrService.showErrorToast('Error', data.message);
            }
          },
          error: (error: any) => {
            this.spinner.hide();
            this.toastrService.showErrorToast('Error', "SomethingWentWrong");
          }
        });
  }

  CloseInvoice(data: any) {
    this.showInvoice = false;
  }
}
